/**
 * Pendant hang planning — how long the stem / chain / cord should be so the
 * shade bottom clears a target height above the floor.
 *
 * Catalog pendant GLBs are modelled at FULL cord (72–105" tall bboxes). The
 * catalog's `materialRegions` split them into canopy / stem / shade bands;
 * the renderer honours a per-object `stemLengthInches` by shortening only
 * the stem band (`applyMaterialRegions`). This module decides that number.
 *
 * Callers pick the target: 72" over a kitchen counter
 * (`KITCHEN_PENDANT_BOTTOM_AFF_INCHES`), 80" over a vanity sink where someone
 * stands (`VANITY_PENDANT_BOTTOM_AFF_INCHES`). When even the minimum stem can't
 * reach the target (`fits: false`) the caller decides: the vanity demotes to a
 * flush fixture, the kitchen keeps the shortest stem.
 */

const PENDANT_STEM_LENGTH_INCHES = 18; // unknown ceiling height
const PENDANT_NONSTEM_FALLBACK_INCHES = 18; // canopy + shade when no regions
const PENDANT_MIN_STEM_INCHES = 3; // never invert / sit on the counter
const PENDANT_MAX_STEM_INCHES = 60; // don't run away on cathedral ceilings

/** canopy + shade height from the model's regions; null without a stem. */
function pendantNonStemHeightInches(model) {
  const mr = model?.materialRegions;
  if (!Array.isArray(mr) || mr.length === 0) return null;
  const stem = mr.find((r) => r.semantic === 'stem');
  if (!stem || typeof stem.yMin !== 'number' || typeof stem.yMax !== 'number') {
    return null;
  }
  const tops = mr.map((r) => r.yMax).filter((v) => typeof v === 'number');
  const bots = mr.map((r) => r.yMin).filter((v) => typeof v === 'number');
  if (!tops.length || !bots.length) return null;
  const total = Math.max(...tops) - Math.min(...bots);
  const nonStem = total - (stem.yMax - stem.yMin);
  return nonStem > 0 ? nonStem : null;
}

/**
 * Stem length that puts the shade bottom at `bottomAboveFloorInches`, clamped
 * to [MIN, MAX]; the default stem when the ceiling height is unknown.
 */
function stemLengthForTarget({
  ceilingHeightInches,
  nonStemHeightInches,
  bottomAboveFloorInches,
}) {
  if (!(ceilingHeightInches > 0)) return PENDANT_STEM_LENGTH_INCHES;
  const nonStem =
    nonStemHeightInches > 0
      ? nonStemHeightInches
      : PENDANT_NONSTEM_FALLBACK_INCHES;
  const stem = ceilingHeightInches - bottomAboveFloorInches - nonStem;
  return Math.max(
    PENDANT_MIN_STEM_INCHES,
    Math.min(PENDANT_MAX_STEM_INCHES, stem)
  );
}

function hasStemRegion(model) {
  return (model?.materialRegions || []).some((r) => r?.semantic === 'stem');
}

/**
 * @param {Object} p
 * @param {number} [p.ceilingHeightInches] - floor to ceiling; unknown → default stem
 * @param {Object} p.model - catalog model ({ materialRegions, dimensions })
 * @param {number} p.bottomAboveFloorInches - where the shade bottom must clear
 * @returns {{ hasStem: boolean, stemLengthInches?: number, heightInches: number,
 *   bottomAboveFloorInches: number|null, fits: boolean }}
 */
function planPendantHang({
  ceilingHeightInches,
  model,
  bottomAboveFloorInches,
}) {
  const knownCeiling = ceilingHeightInches > 0;
  if (!hasStemRegion(model)) {
    const heightInches = model?.dimensions?.height ?? 0;
    const bottom = knownCeiling ? ceilingHeightInches - heightInches : null;
    return {
      hasStem: false,
      heightInches,
      bottomAboveFloorInches: bottom,
      fits: bottom == null ? true : bottom >= bottomAboveFloorInches,
    };
  }
  const nonStem = pendantNonStemHeightInches(model);
  const stemLengthInches = stemLengthForTarget({
    ceilingHeightInches,
    nonStemHeightInches: nonStem,
    bottomAboveFloorInches,
  });
  const heightInches =
    (nonStem ?? PENDANT_NONSTEM_FALLBACK_INCHES) + stemLengthInches;
  const bottom = knownCeiling ? ceilingHeightInches - heightInches : null;
  return {
    hasStem: true,
    stemLengthInches,
    heightInches,
    bottomAboveFloorInches: bottom,
    fits: bottom == null ? true : bottom + 1e-9 >= bottomAboveFloorInches,
  };
}

module.exports = {
  pendantNonStemHeightInches,
  planPendantHang,
  stemLengthForTarget,
  hasStemRegion,
  PENDANT_STEM_LENGTH_INCHES,
  PENDANT_NONSTEM_FALLBACK_INCHES,
  PENDANT_MIN_STEM_INCHES,
  PENDANT_MAX_STEM_INCHES,
};
